import * as React from "react";
import * as ReactDOM from "react-dom";
import * as moment from "moment";
import * as modelsGet from "../../models/get";
import * as apiEvents from "../../api/events";
import { Window } from "../window";
import { Loader, LoaderStyle } from "../loader";
import { Button, ButtonType } from "../button";
import { FormGroup } from "../form";
import { InputMask, InputSize } from "../input";
import { Alert, AlertType } from "../alert";
import { TextArea } from "../textArea";
import { CheckBox } from "../checkbox";

interface IProperties
{
    /**
     * Die ID des Termins der geöffnet werden soll. (Ist leer wenn ein neuer Termin erstellt werden soll.)
     */
    appointmentId?: number,

    /**
     * Gibt an, ob der Termin der gelöscht werden soll, ein archivierter Termin ist.
     */
    isArchived?: boolean,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (deleted?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt an, ob die Checkbox sichtbar ist.
     */
    isCheckboxVisible?: boolean,

    /**
     * Der Termin der gelöscht werden soll.
     */
    appointment?: modelsGet.IAppointment,

    /**
     * Enthält die ID eines verlinkten Termins.
     */
    linkedAppointmentId?: number,

    /**
     * Der Grund für die Löschung.
     */
    reason?: string,

    /**
     * Gibt an, ob auch der verlinkte Termin gelöscht werden soll.
     */
    deleteLinkedAppointmentAlso?: boolean,

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export class DeleteDialog extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see DeleteDialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);
        this.state = {
            isLoading: true,
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        // Termin laden.
        var appointment: modelsGet.IAppointment = await apiEvents.getEvent(props.appointmentId);

        // Links laden.
        var links: modelsGet.ILink[] = await apiEvents.getEventLinks(appointment.id, appointment.parent_event_id);

        // Prüfen ob der Termin auch einen Erst- oder Folgetermin hat.
        var linkedAppointmentId: number;
        if (links != null && links.length > 0)
        {
            linkedAppointmentId = links.where(l => l.event_id !== appointment.id).first().event_id;
        }

        this.setState({
            isLoading: false,
            appointment: appointment,
            linkedAppointmentId: linkedAppointmentId,
            isCheckboxVisible: linkedAppointmentId > 0
        });
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (deleted: boolean = false) =>
    {
        const { onClose } = this.props;
        onClose && onClose(deleted);
        return true;
    }

    /**
     * Ändert den geöffneten Termin oder löscht diesen.
     */
    private onDeleteClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { appointment, linkedAppointmentId, reason, deleteLinkedAppointmentAlso } = this.state;

            if (appointment.is_archived)
            {
                await apiEvents.remove(appointment.id);
            }
            else
            {
                await apiEvents.archive(appointment.id, {
                    delete_reason: reason
                });

                if (deleteLinkedAppointmentAlso)
                {
                    await apiEvents.archive(linkedAppointmentId, {
                        delete_reason: reason
                    });
                }
            }

            // Fenster schließen.
            this.onClosing(true);
        }
        catch (e)
        {
            if (e != null && e.responseJSON == null)
                console.error(e);

            if (e != null && e.responseJSON != null)
            {
                var errors = e.responseJSON;
                var newErrors = {};
                var alertMessage: string;

                for (let error in errors)
                {
                    if (errors[error] != null && errors[error].length > 0)
                        newErrors[error] = errors[error].first();
                }

                for (let error in newErrors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = newErrors[error];
                            break;
                    }
                }

                this.setState(() =>
                {
                    const newState: IState = {};
                    newState.isLoading = false;
                    newState.errors = newErrors;
                    newState.alertMessage = alertMessage;
                    newState.alertType = AlertType.error;
                    return newState;
                });
            }
        }
    }

    private onReasonTextChanged = (value: any[], originalValue: string[]) =>
    {
        this.setState({
            reason: value[0]
        });
    }

    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState({
            deleteLinkedAppointmentAlso: value
        });
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { appointmentId, isArchived } = this.props;
        const { isLoading, isCheckboxVisible, reason, deleteLinkedAppointmentAlso, errors, alertMessage, alertType } = this.state;
        var windowHeight: number = 360;

        if (!isArchived && isCheckboxVisible)
            windowHeight = 380;
        else if (!isArchived)
            windowHeight = 360;
        else if (isArchived)
            windowHeight = 180;

        if (!isLoading)
        {
            icon = "delete";
            title = "Termin #" + appointmentId + " löschen";
        }
        else
        {

            var icon: string = "loading";
            var title: string = "Daten werden geladen...";

            return (
                <Window
                    isOpen={true}
                    titleIcon={icon}
                    title={title}
                    onClosing={this.onClosing}
                    isMaximized={false}
                    isResizable={false}
                    width={600}
                    height={360}>
                    <Loader loaderStyle={LoaderStyle.style1} />
                </Window>
            );
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                isResizable={false}
                width={600}
                height={windowHeight}>
                <div className="delete-dialog">
                    <div className="delete-dialog_content">
                        <p>Wollen Sie den Termin wirklich Löschen?</p>
                        <FormGroup>
                            {
                                !isArchived &&
                                <TextArea
                                    rows={10}
                                    placeholder="Geben Sie den Grund für das löschen des Termins an."
                                    value={reason}
                                    onTextChange={this.onReasonTextChanged}
                                    errorMessage={errors["Reason"]} />
                            }
                            {
                                isCheckboxVisible &&
                                <CheckBox
                                    label="Erst- oder Folgetermin ebenfalls löschen."
                                    isChecked={deleteLinkedAppointmentAlso}
                                    onCheckedChanged={this.onCheckboxChanged} />
                            }
                        </FormGroup>
                    </div>
                    <div className="delete-dialog_actions">
                        <Button
                            text="Löschen"
                            type={ButtonType.danger}
                            isDisabled={!isArchived && (reason == null || reason === "")}
                            onClick={this.onDeleteClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}